import React, { useState, useEffect } from 'react';
import { auth, db, storage, functions } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateEmail, updateProfile, signOut, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import ProfilePicture from '../components/ProfilePicture';
import './Profile.css';
import { normalizeString, formatCPF, formatPhone } from '../utils/customFunctions';

export default function Profile() {
  const [userData, setUserData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      
      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Format phone and CPF for display
          const formattedData = {
            ...data,
            document_number: data.document_number ? formatCPF(data.document_number) : '',
            phone_number: data.phone_number ? formatPhone(data.phone_number) : ''
          };
          setUserData(formattedData);
          setOriginalData(formattedData);
        }
      } catch (error) {
        setMessage({ type: 'error', text: 'Falha ao carregar dados do perfil.' });
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Apply CPF mask
    if (name === 'document_number') {
      formattedValue = formatCPF(value);
    }
    
    // Apply phone mask
    if (name === 'phone_number') {
      formattedValue = formatPhone(value);
    }
    
    setUserData({ ...userData, [name]: formattedValue });
    // Clear message when user makes changes
    setMessage({ type: '', text: '' });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setMessage({ type: 'error', text: 'O tamanho da foto deve ser menor que 5MB.' });
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photo) return null;
    
    setUploadingPhoto(true);
    try {
      const photoRef = ref(storage, `users/${auth.currentUser.uid}/profile/${Date.now()}_${photo.name}`);
      await uploadBytes(photoRef, photo);
      const photoURL = await getDownloadURL(photoRef);
      await updateProfile(auth.currentUser, { photoURL });
      return photoURL;
    } catch (error) {
      throw new Error('Falha ao enviar foto. Tente novamente.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      let photoURL = userData.photo_url;
      
      // Upload photo if changed
      if (photo) {
        photoURL = await uploadPhoto();
      }

      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, { 
        displayName: userData.display_name 
      });

      // Update email if changed
      if (userData.email !== originalData.email) {
        await updateEmail(auth.currentUser, userData.email);
      }

      // Update Firestore document
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        display_name: userData.display_name,
        display_name_normalized: normalizeString(userData.display_name),
        document_number: userData.document_number.replace(/\D/g, ''), // Store only numbers
        phone_number: userData.phone_number.replace(/\D/g, ''), // Store only numbers
        email: userData.email,
        photo_url: photoURL,
        updated_time: new Date(),
      });

      setUserData(prev => ({ ...prev, photo_url: photoURL }));
      setOriginalData({ ...userData, photo_url: photoURL });
      setPhoto(null);
      setPhotoPreview(null);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Falha ao atualizar perfil. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setUserData(originalData);
    setPhoto(null);
    setPhotoPreview(null);
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      setMessage({ type: 'error', text: 'Falha ao sair. Tente novamente.' });
    }
  };

  const hasChanges = () => {
    if (!originalData || !userData) return false;
    return JSON.stringify(userData) !== JSON.stringify(originalData) || photo !== null;
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteAccount = () => {
    setShowDeleteModal(false);
    setShowReauthModal(true);
  };

  const handleReauthentication = async (e) => {
    e.preventDefault();
    setIsDeleting(true);
    setMessage({ type: '', text: '' });

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        deletePassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Call deleteUserData cloud function
      const deleteUserData = httpsCallable(functions, 'deleteUserData');
      await deleteUserData({ userId: auth.currentUser.uid });

      // Delete the user account
      await deleteUser(auth.currentUser);

      // Navigate to login
      navigate('/login');
    } catch (error) {
      console.error('Account deletion failed:', error);
      let errorMessage = 'Falha ao excluir conta. Tente novamente.';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Senha incorreta. Tente novamente.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Por favor, faça login novamente antes de excluir sua conta.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Função de exclusão não encontrada. Entre em contato com o suporte.';
      } else if (error.code === 'functions/internal') {
        errorMessage = 'Erro interno do servidor. Entre em contato com o suporte.';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteAccount = () => {
    setShowDeleteModal(false);
    setShowReauthModal(false);
    setDeletePassword('');
    setMessage({ type: '', text: '' });
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-card">
          <i className="fas fa-spinner fa-spin fa-2x"></i>
          <p>Carregando seu perfil...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="error-card">
          <h2>Perfil Não Encontrado</h2>
          <p>Não foi possível carregar os dados do seu perfil.</p>
          <button onClick={() => navigate('/login')} className="secondary-button">
            Voltar ao Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-photo-section">
            <div className="photo-container">
              <ProfilePicture 
                src={photoPreview || userData.photo_url}
                alt={userData.display_name}
                size="large"
              />
              {uploadingPhoto && (
                <div className="photo-overlay">
                  <div className="spinner"></div>
                </div>
              )}
            </div>
            {isEditing && (
              <div className="photo-upload">
                <label htmlFor="photo-input" className="photo-upload-button">
                  <i className="fas fa-camera"></i> Alterar Foto
                </label>
                <input 
                  id="photo-input"
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
          
          <div className="profile-info">
            <h1>{userData.display_name}</h1>
            <p className="profile-email">{userData.email}</p>
            <span className="profile-role">{userData.role}</span>
          </div>

          <div className="profile-actions">
            {!isEditing ? (
              <>
                <button 
                  onClick={() => setIsEditing(true)} 
                  className="primary-button"
                >
                  <i className="fas fa-edit"></i> Editar Perfil
                </button>
                <button 
                  onClick={handleLogout} 
                  className="logout-button"
                >
                  <i className="fas fa-sign-out-alt"></i> Sair
                </button>
              </>
            ) : (
              <div className="edit-actions">
                <button 
                  onClick={handleCancel} 
                  className="secondary-button"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleSave} 
                  className="primary-button"
                  disabled={saving || !hasChanges()}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            <span className="message-icon">
              <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
            </span>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave} className="profile-form">
          <div className="form-section">
            <h3>Informações Pessoais</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="display_name">Nome Completo</label>
                <input
                  id="display_name"
                  name="display_name"
                  type="text"
                  value={userData.display_name || ''}
                  onChange={handleChange}
                  disabled={!isEditing}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Endereço de E-mail</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={userData.email || ''}
                  onChange={handleChange}
                  disabled={!isEditing}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="document_number">Número do Documento</label>
                <input
                  id="document_number"
                  name="document_number"
                  type="text"
                  value={userData.document_number || ''}
                  onChange={handleChange}
                  disabled={!isEditing}
                  maxLength="14"
                  placeholder="000.000.000-00"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone_number">Número de Telefone</label>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  value={userData.phone_number || ''}
                  onChange={handleChange}
                  disabled={!isEditing}
                  maxLength="15"
                  placeholder="(11) 99999-9999"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Detalhes da Conta</h3>
            <div className="account-details">
              <div className="detail-item">
                <span className="detail-label">Conta Criada:</span>
                <span className="detail-value">
                  {userData.created_time?.toDate?.()?.toLocaleDateString('pt-BR') || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Última Atualização:</span>
                <span className="detail-value">
                  {userData.updated_time?.toDate?.()?.toLocaleDateString('pt-BR') || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Função do Usuário:</span>
                <span className="detail-value role-badge">{userData.role}</span>
              </div>
            </div>
          </div>

          {/* Danger Zone - Account Deletion */}
          <div className="form-section danger-zone">
            <h3>⚠️ Zona de Perigo</h3>
            <div className="danger-zone-content">
              <div className="danger-zone-info">
                <h4>Excluir Conta</h4>
                <p>Esta ação é irreversível e excluirá permanentemente sua conta e todos os dados associados.</p>
              </div>
              <button 
                onClick={handleDeleteAccount} 
                className="delete-account-button"
              >
                <i className="fas fa-trash"></i> Excluir Conta
              </button>
            </div>
          </div>
        </form>

        {/* Delete Account Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>⚠️ Excluir Conta</h3>
                <button onClick={cancelDeleteAccount} className="modal-close">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Atenção:</strong> Esta ação é irreversível!</p>
                <p>Ao excluir sua conta, todos os seus dados serão permanentemente removidos de nossos servidores, incluindo:</p>
                <ul>
                  <li>Informações pessoais e de perfil</li>
                  <li>Histórico de atividades</li>
                  <li>Configurações e preferências</li>
                  <li>Todos os dados associados à sua conta</li>
                </ul>
                <p>Tem certeza de que deseja continuar?</p>
              </div>
              <div className="modal-actions">
                <button onClick={cancelDeleteAccount} className="secondary-button">
                  Cancelar
                </button>
                <button onClick={confirmDeleteAccount} className="delete-button">
                  <i className="fas fa-trash"></i> Sim, Excluir Conta
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reauthentication Modal */}
        {showReauthModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>🔒 Confirmar Identidade</h3>
                <button onClick={cancelDeleteAccount} className="modal-close">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleReauthentication}>
                <div className="modal-body">
                  <p>Por segurança, confirme sua senha para prosseguir com a exclusão da conta:</p>
                  <div className="form-group">
                    <label htmlFor="delete-password">Senha Atual</label>
                    <input
                      id="delete-password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      required
                      placeholder="Digite sua senha atual"
                      disabled={isDeleting}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button 
                    type="button" 
                    onClick={cancelDeleteAccount} 
                    className="secondary-button"
                    disabled={isDeleting}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className="delete-button"
                    disabled={isDeleting || !deletePassword.trim()}
                  >
                    {isDeleting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Excluindo...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash"></i> Confirmar Exclusão
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
